import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MAX_VARIANTS, type AuthoringDoc } from '@personalizehq/editor';
import { FlaskConical, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

// THE VARIANT SWITCHER (ADR 0015, V2). A variant is an alternative step-list of the SAME experience,
// so switching one is just focusing a step inside it — the strip, the artboard, the inspector and
// every node op follow `focusedStepId` and need to know nothing about variants. This control is the
// only place the concept surfaces in the builder chrome.
//
// The KEY is minted at create and immutable after (it is the analytics identity every exposure event
// carries — ADR 0016); rename touches the display name only. Delete is allowed even while a campaign
// splits on the key: the editor cannot see campaigns, and publish refuses a dangling arm loudly —
// the confirm dialog says exactly that instead of pretending the door is locked.

/** The Radix no-empty-string rule vs "the default steps have no key": same sentinel trick the
 *  campaign form uses for optional links. Never a legal variant key (schema charset bars `$`). */
const DEFAULT_SENTINEL = '$default';

/** An external ask to open a dialog (the command palette's path into this control). The `nonce`
 *  makes a repeated ask observable — same-kind requests must still reopen. */
export interface VariantDialogRequest {
  kind: 'create' | 'delete';
  nonce: number;
}

interface VariantSwitcherProps {
  doc: AuthoringDoc;
  activeKey: string | null;
  onSwitch: (key: string | null) => void;
  onCreate: (name: string) => void;
  onRename: (key: string, name: string) => void;
  onDelete: (key: string) => void;
  dialogRequest?: VariantDialogRequest | null;
}

export function VariantSwitcher({
  doc,
  activeKey,
  onSwitch,
  onCreate,
  onRename,
  onDelete,
  dialogRequest,
}: VariantSwitcherProps): React.JSX.Element {
  const variants = doc.variants ?? [];
  const active = variants.find((candidate) => candidate.key === activeKey);
  const [dialog, setDialog] = useState<'create' | 'rename' | 'delete' | null>(null);
  const [name, setName] = useState('');

  const atLimit = variants.length >= MAX_VARIANTS;
  const openCreate = (): void => {
    setName('');
    setDialog('create');
  };

  // A NEW external request opens its dialog — the sanctioned adjust-during-render shape ("storing
  // information from previous renders"), not an effect: React restarts the render before commit,
  // so nothing flashes. The guards mirror the command availability.
  const [handledNonce, setHandledNonce] = useState<number | null>(null);
  if (dialogRequest && dialogRequest.nonce !== handledNonce) {
    setHandledNonce(dialogRequest.nonce);
    if (dialogRequest.kind === 'create' && !atLimit) {
      setName('');
      setDialog('create');
    }
    if (dialogRequest.kind === 'delete' && activeKey) setDialog('delete');
  }

  return (
    <div className="flex shrink-0 items-center gap-1">
      <FlaskConical className="size-3.5 text-muted-foreground" aria-hidden />
      <Select
        value={activeKey ?? DEFAULT_SENTINEL}
        onValueChange={(value) => onSwitch(value === DEFAULT_SENTINEL ? null : value)}
      >
        <SelectTrigger aria-label="Variant" className="h-8 w-36 text-[12px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={DEFAULT_SENTINEL}>Original</SelectItem>
          {variants.map((variant) => (
            <SelectItem key={variant.key} value={variant.key}>
              {variant.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Variant actions" className="size-8 text-muted-foreground">
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem
            disabled={atLimit}
            title={atLimit ? `An experience may hold at most ${MAX_VARIANTS} variants` : undefined}
            onSelect={openCreate}
          >
            <Plus className="size-3.5" /> New variant
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!active}
            onSelect={() => {
              if (!active) return;
              setName(active.name);
              setDialog('rename');
            }}
          >
            <Pencil className="size-3.5" /> Rename variant
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!active}
            className="text-destructive focus:text-destructive"
            onSelect={() => setDialog('delete')}
          >
            <Trash2 className="size-3.5" /> Delete variant
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Create + rename share the one-field dialog; they differ only in verb and commit. */}
      <Dialog open={dialog === 'create' || dialog === 'rename'} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{dialog === 'create' ? 'New variant' : 'Rename variant'}</DialogTitle>
            <DialogDescription>
              {dialog === 'create'
                ? `Duplicates the ${active ? `“${active.name}”` : 'original'} steps as a new arm you can edit independently. Theme and tokens stay shared.`
                : 'Display name only — the variant’s key (its analytics identity) never changes.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="variant-name">Name</Label>
            <Input
              id="variant-name"
              value={name}
              autoFocus
              placeholder="Shorter copy"
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && name.trim()) commit();
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button disabled={!name.trim()} onClick={commit}>
              {dialog === 'create' ? 'Create' : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === 'delete'} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete “{active?.name}”?</DialogTitle>
            <DialogDescription>
              Its steps are removed from this experience (undoable with ⌘Z). Any campaign still
              splitting traffic on this variant will refuse to publish until its experiment is
              updated.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (active) onDelete(active.key);
                setDialog(null);
              }}
            >
              Delete variant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  function commit(): void {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (dialog === 'create') onCreate(trimmed);
    else if (dialog === 'rename' && active) onRename(active.key, trimmed);
    setDialog(null);
  }
}
